import { format } from "date-fns";
import { Employee, EmployeeStatus } from "../types/processTypes";

const AFTER_FLIGHT_EMPLOYEE_STATUSES = new Set<EmployeeStatus>([
  EmployeeStatus.lost,
  EmployeeStatus.returned,
  EmployeeStatus.has_complain,
  EmployeeStatus.contract_completed,
  EmployeeStatus.other,
  EmployeeStatus.departed,
]);

function laterPipelineDate(
  a?: Date | string,
  b?: Date | string,
): Date | string | undefined {
  const da = a ? new Date(a) : undefined;
  const db = b ? new Date(b) : undefined;
  if (!da && !db) return undefined;
  if (!da || Number.isNaN(da.getTime())) return b;
  if (!db || Number.isNaN(db.getTime())) return a;
  return da.getTime() >= db.getTime() ? a : b;
}

export function isPreSelectionEmployee(emp: Employee): boolean {
  const status = emp.status ?? EmployeeStatus.new;
  return status === EmployeeStatus.pending || status === EmployeeStatus.new;
}

export function getEmployeeListDate(
  emp: Employee,
): Date | string | undefined {
  if (isPreSelectionEmployee(emp)) {
    return emp.createdAt;
  }
  return getEmployeeStatusDate(emp);
}

function getLegacyTerminalDate(emp: Employee): Date | string | undefined {
  if (emp.status === EmployeeStatus.discontinued) {
    return emp.additionalData?.discontinuedDate;
  }
  if (emp.status && AFTER_FLIGHT_EMPLOYEE_STATUSES.has(emp.status)) {
    return emp.additionalData?.afterFlightStatusDate;
  }
  return undefined;
}

export function getEmployeeStatusDate(
  emp: Employee,
): Date | string | undefined {
  const milestoneDate =
    emp.status && emp.statusDates
      ? emp.statusDates[emp.status as keyof typeof emp.statusDates]
      : undefined;

  const pipelineDate =
    emp.status === EmployeeStatus.visa_issued
      ? emp.visaIssuedDate
      : emp.status === EmployeeStatus.labour_issued
        ? emp.lmisIssuedDate
        : emp.status === EmployeeStatus.ticketing
          ? laterPipelineDate(emp.visaIssuedDate, emp.lmisIssuedDate)
          : emp.status === EmployeeStatus.flight_booked
            ? emp.flight?.flightDateTime
            : emp.status === EmployeeStatus.departed
              ? emp.statusDates?.departed
              : undefined;

  return (
    milestoneDate ??
    pipelineDate ??
    getLegacyTerminalDate(emp) ??
    emp.currentStatusDate
  );
}

export function formatEmployeeListDate(emp: Employee): string {
  const listDate = getEmployeeListDate(emp);
  if (!listDate) return "—";
  return format(new Date(listDate), "dd-MMM-yyyy").toUpperCase();
}

export function getEmployeeStatusRemark(emp: Employee): string | undefined {
  if (emp.status === EmployeeStatus.discontinued) {
    return (
      emp.currentStatusRemark ?? emp.additionalData?.discontinuedRemark
    );
  }
  if (emp.status && AFTER_FLIGHT_EMPLOYEE_STATUSES.has(emp.status)) {
    return (
      emp.currentStatusRemark ?? emp.additionalData?.afterFlightStatusRemark
    );
  }
  return emp.currentStatusRemark;
}
